import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { assets, testimonials } from "@/lib/site-data";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn(
            "size-4",
            i < rating ? "fill-primary text-primary" : "fill-muted text-muted",
          )}
        />
      ))}
    </div>
  );
}

export function Reviews() {
  const isMobile = useIsMobile();
  const perView = isMobile ? 1 : 2;
  const maxIndex = Math.max(0, testimonials.length - perView);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  const go = (dir: number) => setIndex((i) => Math.min(Math.max(i + dir, 0), maxIndex));

  return (
    <section id="referencie" className="bg-background py-20 md:py-28">
      <div className="container-artbaz content-artbaz">
        <Reveal className="max-w-3xl">
          <span className="eyebrow">Ocenenia a recenzie</span>
          <h2 className="mt-6 text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Ocenená kvalita, ktorú potvrdzujú naši klienti
          </h2>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Dlhodobá spokojnosť klientov a odborné ocenenia sú pre nás najlepším dôkazom kvality
            našej práce. Vážime si dôveru, ktorú nám naši zákazníci preukazujú.
          </p>
        </Reveal>

        {/* Rating + Awards — open, badge-led layout */}
        <div className="mt-14 flex flex-col items-stretch gap-10 lg:flex-row lg:items-center lg:gap-16">
          {/* Rating highlight */}
          <Reveal className="flex shrink-0 items-center gap-6">
            <div className="flex h-28 w-28 flex-col items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-dark text-primary-foreground shadow-[var(--shadow-primary)]">
              <span className="text-4xl font-extrabold leading-none tracking-tight">4,8</span>
              <span className="mt-1 text-xs font-semibold opacity-90">z 5 hviezd</span>
            </div>
            <div>
              <Stars rating={5} />
              <p className="mt-3 max-w-[14rem] text-sm font-semibold leading-snug text-muted-foreground">
                27 overených recenzií od spokojných klientov
              </p>
            </div>
          </Reveal>

          {/* Divider */}
          <div className="hidden h-20 w-px shrink-0 bg-border lg:block" />

          {/* Awards — floating badges, no boxed frame */}
          <Reveal delay={100} className="flex-1">
            <p className="mb-6 text-xs font-bold uppercase tracking-[0.18em] text-primary-dark">
              Ocenenie Orly stavebníctva
            </p>
            <div className="flex flex-wrap items-center gap-10 sm:gap-14">
              {[
                { src: assets.orly2021, label: "Laureát 2021" },
                { src: assets.orly2023, label: "Laureát 2023" },
              ].map((award) => (
                <div key={award.label} className="group flex items-center gap-4">
                  <img
                    src={award.src}
                    alt={`Orly stavebníctva — ${award.label}`}
                    className="h-28 w-auto drop-shadow-lg transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-105 md:h-36"
                  />
                  <div>
                    <p className="text-base font-bold text-foreground">{award.label}</p>
                    <p className="text-sm text-muted-foreground">Orly stavebníctva</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>


        {/* Testimonials carousel */}
        <Reveal className="mt-14">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
              style={{ transform: `translateX(-${index * (100 / perView)}%)` }}
            >
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="w-full shrink-0 px-3 first:pl-0 last:pr-0 md:w-1/2"
                >
                  <article className="flex h-full flex-col rounded-2xl border border-border bg-card p-8 shadow-[var(--shadow-card)]">
                    <Quote className="size-9 text-primary" />
                    <p className="mt-5 flex-1 text-base leading-relaxed text-foreground/90">
                      „{t.text}"
                    </p>
                    <div className="mt-6 border-t border-border pt-5">
                      <Stars rating={t.rating} />
                      <p className="mt-3 font-bold text-foreground">{t.name}</p>
                      <p className="text-sm text-muted-foreground">{t.role}</p>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <button
              type="button"
              onClick={() => go(-1)}
              disabled={index === 0}
              aria-label="Predchádzajúce recenzie"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-border text-foreground transition-all duration-300 hover:-translate-y-0.5 hover:border-primary hover:text-primary-dark disabled:pointer-events-none disabled:opacity-40"
            >
              <ChevronLeft className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              disabled={index >= maxIndex}
              aria-label="Ďalšie recenzie"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-border text-foreground transition-all duration-300 hover:-translate-y-0.5 hover:border-primary hover:text-primary-dark disabled:pointer-events-none disabled:opacity-40"
            >
              <ChevronRight className="size-5" />
            </button>
            <div className="ml-2 flex gap-2">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Skupina recenzií ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={cn(
                    "h-1.5 rounded-full transition-all duration-300",
                    i === index ? "w-8 bg-primary" : "w-3 bg-border",
                  )}
                />
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
